<template>
  <teleport to="body">
    <div
      v-if="isLoading && computedIsApp"
      class="fixed inset-0 bg-slate-50/50 z-999999 flex items-center justify-center loading-dim"
    >
      <svg class="loading-spinner" viewBox="0 0 50 50">
        <circle class="loading-circle" cx="25" cy="25" r="20" />
      </svg>

      <p v-if="showPercentage" class="loading-percent">{{ isDownloading }}%</p>

      <div class="loading-steps">
        <transition name="step-current">
          <div
            v-if="visibleSteps[0]"
            :key="visibleSteps[0].id"
            class="step-item step-current step-position-0"
          >
            {{ visibleSteps[0].text }}
          </div>
        </transition>

        <transition name="step-next">
          <div
            v-if="visibleSteps[1]"
            :key="visibleSteps[1].id"
            class="step-item step-next step-position-1"
          >
            {{ visibleSteps[1].text }}
          </div>
        </transition>

        <transition name="step-future">
          <div
            v-if="visibleSteps[2]"
            :key="visibleSteps[2].id"
            class="step-item step-future step-position-2"
          >
            {{ visibleSteps[2].text }}
          </div>
        </transition>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
  import { isApp } from '@/index'
  import { computed, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useAllTaskStore } from '@/app/store/task'

  const props = defineProps<{
    isLoading: boolean
  }>()

  watch(
    () => props.isLoading,
    () => {
      if (props.isLoading) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
    },
  )

  const allTaskStore = useAllTaskStore()

  const { previousPhotoProgress, floorplanProgress, downloadPhase } = storeToRefs(allTaskStore)

  const computedIsApp = computed(() => {
    return isApp()
  })

  // downloadPhase: -1=비활성, 0=초기화, 1=도면·시설물사진, 2=결함사진
  const showPercentage = computed(
    () => downloadPhase.value >= 1 && downloadPhase.value <= 2 && isDownloading.value > 0,
  )

  const isDownloading = computed(() => {
    let raw = 0
    if (downloadPhase.value === 1) {
      raw = floorplanProgress.value
    } else if (downloadPhase.value === 2) {
      raw = previousPhotoProgress.value
    }
    // 최대 99%까지만 표시
    return Math.min(raw, 99)
  })

  // 전체 단계 정의
  const allSteps = [
    { id: 'task-info', text: '과업 정보 로드 중...' },
    {
      id: 'phase1',
      activeText: '도면·시설물 사진 다운로드 중...',
      waitingText: '도면·시설물 사진 다운로드 대기 중...',
    },
    {
      id: 'phase2',
      activeText: '결함 사진 다운로드 중...',
      waitingText: '결함 사진 다운로드 대기 중...',
    },
  ]

  // store에서 직접 단계를 받아 사용
  const currentStepIndex = computed(() => {
    if (downloadPhase.value < 0) return 0
    return downloadPhase.value
  })

  // 표시할 단계들 (현재 + 다음 2단계)
  const visibleSteps = computed(() => {
    const startIndex = currentStepIndex.value
    const steps = []

    for (let i = 0; i < 3; i++) {
      const stepIndex = startIndex + i
      if (stepIndex < allSteps.length) {
        const step = allSteps[stepIndex]
        let text = ''

        if ('text' in step && step.text) {
          text = step.text
        } else if (i === 0) {
          text = ('activeText' in step ? step.activeText : '') ?? ''
        } else {
          text = ('waitingText' in step ? step.waitingText : '') ?? ''
        }

        steps.push({
          id: `${step.id}-${startIndex}`,
          text,
        })
      }
    }

    return steps
  })
</script>

<style scoped lang="scss">
  .loading-dim {
    background-color: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(3px);
  }

  .loading-spinner {
    position: absolute;
    width: 50px;
    height: 50px;
    animation: rotate 1s linear infinite;
  }

  .loading-circle {
    fill: none;
    stroke: var(--Primary-500, #0064ff);
    stroke-width: 6;
    stroke-linecap: round;
    animation: dash 2s ease-in-out infinite;
  }

  .loading-percent {
    position: absolute;
    color: var(--Gray-800);
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .loading-steps {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin-top: 80px;
    width: 300px;
    height: 90px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  .step-item {
    position: absolute;
    text-align: center;
    white-space: nowrap;
    margin: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  // 고정된 위치 정의
  .step-position-0 {
    top: 0;
  }

  .step-position-1 {
    top: 30px;
  }

  .step-position-2 {
    top: 54px;
  }

  // 현재 단계 (가장 크고 진함)
  .step-current {
    font-size: 18px;
    font-weight: 600;
    color: var(--Gray-900);
    opacity: 1;
  }

  // 다음 단계 (중간 크기)
  .step-next {
    font-size: 14px;
    font-weight: 400;
    color: var(--Gray-600);
    opacity: 0.7;
  }

  // 다다음 단계 (가장 작고 연함)
  .step-future {
    font-size: 12px;
    font-weight: 300;
    color: var(--Gray-400);
    opacity: 0.5;
  }

  // 단계별 애니메이션
  .step-current-enter-active,
  .step-current-leave-active,
  .step-next-enter-active,
  .step-next-leave-active,
  .step-future-enter-active,
  .step-future-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .step-current-enter-from,
  .step-next-enter-from,
  .step-future-enter-from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px) scale(0.9);
  }

  .step-current-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px) scale(1.1);
  }

  .step-next-leave-to,
  .step-future-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(10px) scale(0.9);
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes dash {
    0% {
      stroke-dasharray: 1, 150;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -124;
    }
  }
</style>
